import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * <cart-upsell> — cart cross-sell + in-cart collection browser.
 *
 * Default view: complementary categories for the newest cart item, rendered by
 * sections/cart-recommendations.liquid in the seed product's context.
 * Collection tabs: tapping a tab loads that collection's products into the strip
 * in place (fetched via /collections/<handle>?section_id=cart-recommendations).
 *
 * The drawer is a Section-Rendering-API section: every cart change re-renders it
 * and wipes our injected cards. A MutationObserver repaints instantly from cache
 * (no timing race); a cartLinesUpdate listener re-seeds to the newest item. Fails
 * safe: any error hides the block; an empty collection falls back to Suggested.
 */
const cache = new Map();

// Rotation state lives at MODULE scope (not on the element) so it survives Horizon's
// cart-drawer morph — which sometimes replaces the <cart-upsell> node entirely.
let gIndex = 0; // current rotation position across the tabs
let gAdvanceNext = false; // set by an add → the next (re)load advances to a fresh set

class CartUpsell extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #refreshTimer;
  /** @type {MutationObserver | undefined} */
  #observer;
  /** @type {IntersectionObserver | undefined} */
  #visObserver;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #tick;
  #remaining = 30;
  #ROTATE_SECONDS = 30;

  connectedCallback() {
    this.handle = this.dataset.seedHandle;
    this.limit = this.dataset.limit || '6';
    this.activeUrl = this.#defaultUrl();

    this.addEventListener('click', this.#onClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartChange);

    this.#observer = new MutationObserver(this.#onMutate);
    this.#observer.observe(this, { childList: true, subtree: true });

    // Auto-rotate only while the cart is actually on screen (drawer open).
    this.#visObserver = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) this.#startRotation();
      else this.#stopRotation();
    });
    this.#visObserver.observe(this);

    if (!this.handle) {
      this.#hideAll();
      return;
    }
    // A morph-replaced element re-enters here: honour the module-level rotation
    // position, and consume a pending add-advance so a replace still rolls forward.
    if (gAdvanceNext) {
      gAdvanceNext = false;
      gIndex += 1;
    }
    this.#applyIndex();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartChange);
    this.#observer?.disconnect();
    this.#visObserver?.disconnect();
    this.#stopRotation();
    clearTimeout(this.#refreshTimer);
  }

  #tabKeys() {
    return [...this.querySelectorAll('.cart-upsell__tab')].map((t) => t.dataset.collection);
  }

  #renderTimer() {
    const fill = this.querySelector('.cart-upsell__timer-fill');
    if (fill) fill.style.width = `${(this.#remaining / this.#ROTATE_SECONDS) * 100}%`;
    const num = this.querySelector('.cart-upsell__timer-num');
    if (num) num.textContent = `${this.#remaining}s`;
  }

  // Restart the countdown from 30s at the current tab.
  #resetTimer() {
    this.#remaining = this.#ROTATE_SECONDS;
    this.#renderTimer();
  }

  #startRotation() {
    this.#stopRotation();
    const keys = this.#tabKeys();
    const timer = this.querySelector('.cart-upsell__timer');
    if (keys.length <= 1) {
      if (timer) timer.style.display = 'none';
      return;
    }
    if (timer) timer.style.display = '';
    this.#resetTimer();
    this.#tick = setInterval(() => {
      this.#remaining -= 1;
      if (this.#remaining <= 0) this.#advance(); // resets the countdown itself
      this.#renderTimer();
    }, 1000);
  }

  #stopRotation() {
    clearInterval(this.#tick);
    this.#tick = undefined;
  }

  // Select the tab at #index (wrapped) and load its products.
  #applyIndex() {
    const keys = this.#tabKeys();
    if (!keys.length) return;
    gIndex = ((gIndex % keys.length) + keys.length) % keys.length;
    const key = keys[gIndex];
    this.#setActiveTab(key);
    this.#loadUrl(key === 'suggested' ? this.#defaultUrl() : this.#collectionUrl(key));
  }

  // Move to the next set (what a timer tick or an add-from-recs does).
  #advance() {
    gIndex += 1;
    this.#applyIndex();
    this.#resetTimer();
  }

  get #slot() {
    return this.querySelector('[data-slot="cross"]');
  }

  get #root() {
    const raw = (window.Shopify && Shopify.routes && Shopify.routes.root) || this.dataset.root || '/';
    return raw.endsWith('/') ? raw : raw + '/';
  }

  #defaultUrl() {
    return `${this.#root}products/${encodeURIComponent(this.handle)}?section_id=cart-recommendations`;
  }

  #collectionUrl(handle) {
    return `${this.#root}collections/${encodeURIComponent(handle)}?section_id=cart-recommendations`;
  }

  // Repaint the slot the theme wiped (from cache, instant). Only fires on external
  // childList changes — our own writes disconnect the observer while writing.
  #onMutate = () => {
    const slot = this.#slot;
    if (!slot || !this.activeUrl || slot.querySelector('.curec__card')) return;
    const html = cache.get(this.activeUrl);
    if (html && html.indexOf('curec__card') !== -1) this.#writeSlot(slot, html);
  };

  #writeSlot(slot, html) {
    this.#observer?.disconnect();
    slot.innerHTML = html;
    const block = this.#blockOf(slot);
    if (block) block.hidden = false;
    this.#observer?.observe(this, { childList: true, subtree: true });
  }

  #setActiveTab(collection) {
    this.querySelectorAll('.cart-upsell__tab').forEach((tab) => {
      tab.classList.toggle('is-active', tab.dataset.collection === collection);
    });
  }

  #onCartChange = (event) => {
    const schedule = () => {
      clearTimeout(this.#refreshTimer);
      this.#refreshTimer = setTimeout(() => this.#refresh(), 200);
    };
    if (event && event.promise && typeof event.promise.then === 'function') {
      event.promise.then(schedule).catch(() => {});
    } else {
      schedule();
    }
  };

  async #refresh() {
    try {
      const cartUrl = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
      const res = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const items = cart.items || [];
      if (!items.length) {
        this.#hideAll();
        return;
      }
      // Re-seed to the newest cart item.
      this.handle = items[items.length - 1].handle;
      if (gAdvanceNext) {
        // Added from the strip → roll forward to a fresh set (like a timer tick).
        gAdvanceNext = false;
        this.#advance();
      } else {
        // Other cart change (qty/remove) → keep the current view, reload for the new seed.
        this.#applyIndex();
        if (this.#tick) this.#resetTimer();
      }
    } catch (e) {
      /* keep whatever is currently shown */
    }
  }

  #blockOf(slot) {
    return slot ? slot.closest('[data-rec-block]') : null;
  }

  #hideAll() {
    this.querySelectorAll('[data-rec-block]').forEach((block) => {
      block.hidden = true;
    });
  }

  async #loadUrl(url) {
    const slot = this.#slot;
    if (!slot) return;
    this.activeUrl = url;
    try {
      let html = cache.get(url);
      if (html == null) {
        const res = await fetch(url);
        if (!res.ok) {
          this.#fallbackOrHide(url);
          return;
        }
        const raw = await res.text();
        const tmp = document.createElement('template');
        tmp.innerHTML = raw;
        const list = tmp.content.querySelector('.curec__list');
        html = list ? list.outerHTML : '';
        cache.set(url, html);
      }
      if (!html || html.indexOf('curec__card') === -1) {
        this.#fallbackOrHide(url);
        return;
      }
      this.#writeSlot(slot, html);
    } catch (e) {
      this.#fallbackOrHide(url);
    }
  }

  // An empty collection tab shouldn't blank the whole block — snap back to Suggested.
  #fallbackOrHide(url) {
    const def = this.#defaultUrl();
    if (this.handle && url !== def) {
      gIndex = 0;
      this.#setActiveTab('suggested');
      this.#loadUrl(def);
    } else {
      this.#hideAll();
    }
  }

  #onClick = (event) => {
    // A click on a recommendation link (image, title, or "Choose options") counts as
    // cross-sell engagement -> flag the cart so Flow can tag the order
    // "cross-sell-influenced" even if the shopper adds something else on the product page.
    const recLink = event.target.closest('.curec__media, .curec__title, .curec__add--link');
    if (recLink && this.contains(recLink)) {
      this.#markEngaged();
      return; // let the link navigate normally
    }

    // Collection tab → swap the strip to that collection in place.
    const tab = event.target.closest('.cart-upsell__tab');
    if (tab && this.contains(tab)) {
      event.preventDefault();
      const i = this.#tabKeys().indexOf(tab.dataset.collection);
      gIndex = i < 0 ? 0 : i;
      this.#applyIndex();
      this.#resetTimer(); // give the shopper a fresh 30s on their pick
      return;
    }

    // Size selection → turn the pending "Select size" button into a live "Add".
    const sizeBtn = event.target.closest('.curec__size');
    if (sizeBtn && !sizeBtn.hasAttribute('disabled') && this.contains(sizeBtn)) {
      const group = sizeBtn.parentElement;
      group.querySelectorAll('.curec__size').forEach((b) => b.classList.remove('is-selected'));
      sizeBtn.classList.add('is-selected');
      const addBtn = group.parentElement.querySelector('.curec__add[data-needs-size]');
      if (addBtn) {
        addBtn.dataset.variantId = sizeBtn.dataset.variantId;
        addBtn.classList.remove('curec__add--pending');
        addBtn.textContent = 'Add';
      }
      return;
    }

    const addBtn = event.target.closest('.curec__add');
    if (!addBtn || addBtn.tagName !== 'BUTTON' || !this.contains(addBtn)) return;

    // Pending (needs a size, none chosen) → nudge the size row instead of adding.
    if (addBtn.hasAttribute('data-needs-size') && !addBtn.dataset.variantId) {
      event.preventDefault();
      const sizes = addBtn.parentElement.querySelector('.curec__sizes');
      if (sizes) {
        sizes.classList.remove('pulse');
        void sizes.offsetWidth;
        sizes.classList.add('pulse');
      }
      return;
    }

    if (!addBtn.hasAttribute('disabled') && addBtn.dataset.variantId) {
      event.preventDefault();
      this.#add(addBtn);
    }
  };

  // Record cross-sell engagement as a cart attribute (order-level). keepalive lets the
  // POST finish even as the click navigates away to the product page.
  #markEngaged() {
    try {
      const cartUrl = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
      fetch(`${cartUrl}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { cross_sell_engaged: 'true' } }),
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      /* non-blocking */
    }
  }

  async #add(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const previousLabel = btn.textContent;
    btn.setAttribute('disabled', '');
    btn.textContent = 'Adding…';

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ id: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
      // No `sections`: with an empty detail, Horizon re-renders the drawer itself
      // (clean full re-render, avoids the row misalignment a hand-fed morph causes).
      // `_added_from` (leading underscore = hidden from customer) marks the line as a
      // cross-sell purchase. It rides through to the order, where Shopify Flow reads it
      // and adds the "cross-sell" order tag. Per-line, so a later removal drops it too.
      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _added_from: 'Cross-sell' } }],
      });
      const res = await fetch(addUrl, fetchConfig('json', { body }));
      const data = await res.json();

      if (!res.ok || data.status) {
        deferred.reject(new Error(data.description || data.message || 'add failed'));
        btn.textContent = data.description || 'Unavailable';
        setTimeout(() => {
          btn.textContent = previousLabel;
          btn.removeAttribute('disabled');
        }, 1600);
        return;
      }

      // Roll the strip forward to a fresh set on the refresh this add triggers.
      gAdvanceNext = true;
      deferred.resolve({ detail: { source: 'cart-upsell', didError: false } });
    } catch (err) {
      deferred.reject(err);
      btn.textContent = previousLabel;
      btn.removeAttribute('disabled');
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
